import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  useNotify,
  useCreate,
  useDataProvider,
  useRecordContext,
  FileInput,
  FileField,
  useRedirect,
} from "react-admin";
import { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  ListItemButton,
} from "@mui/material";
import { useFormContext } from "react-hook-form";
import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const degreeChoices = [
  { id: "BACHELOR", name: "Бакалавр" },
  { id: "MASTER", name: "Магістр" },
  { id: "COURSE", name: "Курсова робота" },
];

interface Author {
  id: number;
  fullName: string;
  code: string;
}

interface AuthorSelectModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (author: Author) => void;
  onCreate: (author: Author) => void;
}

const AUTHOR_LIST_ROW_HEIGHT = 40; // px
const AUTHOR_LIST_MIN_ROWS = 10;

// Modal for selecting or creating an author
const AuthorSelectModal = ({
  open,
  onClose,
  onSelect,
  onCreate,
}: AuthorSelectModalProps) => {
  const dataProvider = useDataProvider();
  const [searchName, setSearchName] = useState("");
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // Fetch authors filtered by search and filters
  useEffect(() => {
    setLoading(true);
    dataProvider
      .getList("people", {
        pagination: { page: 1, perPage: 20 },
        sort: { field: "fullName", order: "ASC" },
        filter: { type: "AUTHOR", fullName_like: searchName },
      })
      .then(({ data }) => {
        setAuthors(data as Author[]);
        setLoading(false);
      });
  }, [searchName, showCreate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        Select Author
        <Button
          variant="outlined"
          onClick={() => setShowCreate(true)}
          sx={{
            position: "absolute",
            top: 8,
            right: 16,
            zIndex: 2,
            whiteSpace: "nowrap",
            minHeight: 32,
          }}
        >
          CREATE NEW AUTHOR
        </Button>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
          }}
        >
          {/* Left: Search and List */}
          <Box
            sx={{
              flex: 1,
              pr: 2,
              borderRight: showCreate ? "1px solid #eee" : "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                mb: 2,
                alignItems: "center",
              }}
            >
              <TextField
                label="Search author by name"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                fullWidth
              />
            </Box>
            {loading ? (
              <CircularProgress />
            ) : (
              <Box
                sx={{
                  minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  maxHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  overflowY: "auto",
                }}
              >
                <List>
                  {authors.length > 0
                    ? authors.map((author: Author) => (
                        <ListItem
                          disablePadding
                          key={author.id}
                          sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                        >
                          <ListItemButton
                            onClick={() => {
                              onSelect(author);
                              onClose();
                            }}
                          >
                            <ListItemText primary={author.fullName} />
                          </ListItemButton>
                        </ListItem>
                      ))
                    : Array.from({ length: AUTHOR_LIST_MIN_ROWS }).map(
                        (_, idx) => (
                          <ListItem
                            key={idx}
                            sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                          >
                            <ListItemText
                              primary={idx === 0 ? "No authors found" : ""}
                            />
                          </ListItem>
                        )
                      )}
                </List>
              </Box>
            )}
          </Box>
          {/* Right: Creation Form */}
          {showCreate && (
            <Box sx={{ flex: 1, pl: 2 }}>
              <AuthorCreateInline
                onClose={() => setShowCreate(false)}
                onCreated={(author) => {
                  setShowCreate(false);
                  onSelect(author as Author);
                  onClose();
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

interface AuthorCreateInlineProps {
  onClose: () => void;
  onCreated: (author: Author) => void;
}

// Inline author creation form
const AuthorCreateInline = ({
  onClose,
  onCreated,
}: AuthorCreateInlineProps) => {
  const [create, { isLoading }] = useCreate();
  const notify = useNotify();
  const [formData, setFormData] = useState({
    code: "",
    type: "AUTHOR",
    lastName: "",
    firstName: "",
    middleName: "",
    fullName: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const fullName =
        `${formData.lastName} ${formData.firstName} ${formData.middleName}`.trim();
      create(
        "people",
        { data: { ...formData, fullName } },
        {
          onSuccess: (data) => {
            notify("Author created successfully");
            if (data && data.id) {
              onCreated(data as Author);
            } else {
              notify("Error: Author creation response is invalid", {
                type: "error",
              });
            }
          },
          onError: (error) => {
            console.error("Create error:", error);
            notify("Error creating author", { type: "error" });
          },
        }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2, p: 2, border: "1px solid #eee", borderRadius: 2 }}>
      <TextField
        label="Code"
        value={formData.code}
        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Last Name"
        value={formData.lastName}
        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="First Name"
        value={formData.firstName}
        onChange={(e) =>
          setFormData({ ...formData, firstName: e.target.value })
        }
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Middle Name"
        value={formData.middleName}
        onChange={(e) =>
          setFormData({ ...formData, middleName: e.target.value })
        }
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Type"
        value="AUTHOR"
        disabled
        fullWidth
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          Create
        </Button>
      </Box>
    </Box>
  );
};

const AuthorField = ({
  author,
  setAuthor,
}: {
  author: any;
  setAuthor: (a: any) => void;
}) => {
  const [showModal, setShowModal] = useState(false);
  const handleSelect = (selectedAuthor: any) => {
    setAuthor(selectedAuthor);
  };
  return (
    <>
      <TextField
        label="Author"
        value={author ? author.fullName : "Select author..."}
        fullWidth
        margin="normal"
        InputProps={{ readOnly: true }}
        onClick={() => setShowModal(true)}
        sx={{ cursor: "pointer" }}
      />
      <AuthorSelectModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSelect={handleSelect}
        onCreate={handleSelect}
      />
    </>
  );
};

const SupervisorField = ({
  supervisor,
  setSupervisor,
}: {
  supervisor: any;
  setSupervisor: (a: any) => void;
}) => {
  const [showModal, setShowModal] = useState(false);
  const handleSelect = (selectedSupervisor: any) => {
    setSupervisor(selectedSupervisor);
  };
  return (
    <>
      <TextField
        label="Supervisor"
        value={supervisor ? supervisor.fullName : "Select supervisor..."}
        fullWidth
        margin="normal"
        InputProps={{ readOnly: true }}
        onClick={() => setShowModal(true)}
        sx={{ cursor: "pointer" }}
      />
      <SupervisorSelectModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSelect={handleSelect}
        onCreate={handleSelect}
      />
    </>
  );
};

// SupervisorSelectModal and SupervisorCreateInline are clones of AuthorSelectModal/AuthorCreateInline but with type: SUPERVISOR
const SupervisorSelectModal = ({
  open,
  onClose,
  onSelect,
  onCreate,
}: AuthorSelectModalProps) => {
  const dataProvider = useDataProvider();
  const [searchName, setSearchName] = useState("");
  const [supervisors, setSupervisors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    setLoading(true);
    dataProvider
      .getList("people", {
        pagination: { page: 1, perPage: 20 },
        sort: { field: "fullName", order: "ASC" },
        filter: { type: "SUPERVISOR", fullName_like: searchName },
      })
      .then(({ data }) => {
        setSupervisors(data as Author[]);
        setLoading(false);
      });
  }, [searchName, showCreate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        Select Supervisor
        <Button
          variant="outlined"
          onClick={() => setShowCreate(true)}
          sx={{
            position: "absolute",
            top: 8,
            right: 16,
            zIndex: 2,
            whiteSpace: "nowrap",
            minHeight: 32,
          }}
        >
          CREATE NEW SUPERVISOR
        </Button>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
          }}
        >
          {/* Left: Search and List */}
          <Box
            sx={{
              flex: 1,
              pr: 2,
              borderRight: showCreate ? "1px solid #eee" : "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                mb: 2,
                alignItems: "center",
              }}
            >
              <TextField
                label="Search supervisor by name"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                fullWidth
              />
            </Box>
            {loading ? (
              <CircularProgress />
            ) : (
              <Box
                sx={{
                  minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  maxHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  overflowY: "auto",
                }}
              >
                <List>
                  {supervisors.length > 0
                    ? supervisors.map((supervisor: Author) => (
                        <ListItem
                          disablePadding
                          key={supervisor.id}
                          sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                        >
                          <ListItemButton
                            onClick={() => {
                              onSelect(supervisor);
                              onClose();
                            }}
                          >
                            <ListItemText primary={supervisor.fullName} />
                          </ListItemButton>
                        </ListItem>
                      ))
                    : Array.from({ length: AUTHOR_LIST_MIN_ROWS }).map(
                        (_, idx) => (
                          <ListItem
                            key={idx}
                            sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                          >
                            <ListItemText
                              primary={idx === 0 ? "No supervisors found" : ""}
                            />
                          </ListItem>
                        )
                      )}
                </List>
              </Box>
            )}
          </Box>
          {/* Right: Creation Form */}
          {showCreate && (
            <Box sx={{ flex: 1, pl: 2 }}>
              <SupervisorCreateInline
                onClose={() => setShowCreate(false)}
                onCreated={(supervisor) => {
                  setShowCreate(false);
                  onSelect(supervisor as Author);
                  onClose();
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

interface SupervisorCreateInlineProps {
  onClose: () => void;
  onCreated: (supervisor: Author) => void;
}

const SupervisorCreateInline = ({
  onClose,
  onCreated,
}: SupervisorCreateInlineProps) => {
  const [create, { isLoading }] = useCreate();
  const notify = useNotify();
  const [formData, setFormData] = useState({
    code: "",
    type: "SUPERVISOR",
    lastName: "",
    firstName: "",
    middleName: "",
    fullName: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const fullName =
        `${formData.lastName} ${formData.firstName} ${formData.middleName}`.trim();
      create(
        "people",
        { data: { ...formData, fullName } },
        {
          onSuccess: (data) => {
            notify("Supervisor created successfully");
            if (data && data.id) {
              onCreated(data as Author);
            } else {
              notify("Error: Supervisor creation response is invalid", {
                type: "error",
              });
            }
          },
          onError: (error) => {
            console.error("Create error:", error);
            notify("Error creating supervisor", { type: "error" });
          },
        }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2, p: 2, border: "1px solid #eee", borderRadius: 2 }}>
      <TextField
        label="Code"
        value={formData.code}
        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Last Name"
        value={formData.lastName}
        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="First Name"
        value={formData.firstName}
        onChange={(e) =>
          setFormData({ ...formData, firstName: e.target.value })
        }
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Middle Name"
        value={formData.middleName}
        onChange={(e) =>
          setFormData({ ...formData, middleName: e.target.value })
        }
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Type"
        value="SUPERVISOR"
        disabled
        fullWidth
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          Create
        </Button>
      </Box>
    </Box>
  );
};

// SpecialtyField
const SpecialtyField = ({
  specialty,
  setSpecialty,
}: {
  specialty: any;
  setSpecialty: (a: any) => void;
}) => {
  const [showModal, setShowModal] = useState(false);
  const handleSelect = (selectedSpecialty: any) => {
    setSpecialty(selectedSpecialty);
  };
  return (
    <>
      <TextField
        label="Specialty"
        value={
          specialty
            ? `${specialty.name}${
                specialty.comment ? ` — ${specialty.comment}` : ""
              }`
            : "Select specialty..."
        }
        fullWidth
        margin="normal"
        InputProps={{ readOnly: true }}
        onClick={() => setShowModal(true)}
        sx={{ cursor: "pointer" }}
      />
      <SpecialtySelectModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSelect={handleSelect}
        onCreate={handleSelect}
      />
    </>
  );
};

const SpecialtySelectModal = ({ open, onClose, onSelect, onCreate }: any) => {
  const dataProvider = useDataProvider();
  const [searchName, setSearchName] = useState("");
  const [specialties, setSpecialties] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    setLoading(true);
    dataProvider
      .getList("specialties", {
        pagination: { page: 1, perPage: 20 },
        sort: { field: "name", order: "ASC" },
        filter: { name: searchName },
      })
      .then(({ data }) => {
        setSpecialties(data as any[]);
        setLoading(false);
      });
  }, [searchName, showCreate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Specialty</DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
          }}
        >
          {/* Left: Search and List */}
          <Box
            sx={{
              flex: 1,
              pr: 2,
              borderRight: showCreate ? "1px solid #eee" : "none",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Search specialty by name"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                fullWidth
              />
              <Button variant="outlined" onClick={() => setShowCreate(true)}>
                Create New Specialty
              </Button>
            </Box>
            {loading ? (
              <CircularProgress />
            ) : (
              <Box
                sx={{
                  minHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  maxHeight: AUTHOR_LIST_ROW_HEIGHT * AUTHOR_LIST_MIN_ROWS,
                  overflowY: "auto",
                }}
              >
                <List>
                  {specialties.length > 0
                    ? specialties.map((specialty: any) => (
                        <ListItem
                          disablePadding
                          key={specialty.id}
                          sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                        >
                          <ListItemButton
                            onClick={() => {
                              onSelect(specialty);
                              onClose();
                            }}
                          >
                            <ListItemText
                              primary={
                                specialty.comment
                                  ? `${specialty.name} — ${specialty.comment}`
                                  : specialty.name
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ))
                    : Array.from({ length: AUTHOR_LIST_MIN_ROWS }).map(
                        (_, idx) => (
                          <ListItem
                            key={idx}
                            sx={{ height: AUTHOR_LIST_ROW_HEIGHT }}
                          >
                            <ListItemText
                              primary={idx === 0 ? "No specialties found" : ""}
                            />
                          </ListItem>
                        )
                      )}
                </List>
              </Box>
            )}
          </Box>
          {/* Right: Creation Form */}
          {showCreate && (
            <Box sx={{ flex: 1, pl: 2 }}>
              <SpecialtyCreateInline
                onClose={() => setShowCreate(false)}
                onCreated={(specialty: any) => {
                  setShowCreate(false);
                  onSelect(specialty);
                  onClose();
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

const SpecialtyCreateInline = ({ onClose, onCreated }: any) => {
  const [create, { isLoading }] = useCreate();
  const notify = useNotify();
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    comment: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      create(
        "specialties",
        { data: formData },
        {
          onSuccess: (data: any) => {
            notify("Specialty created successfully");
            if (data && data.id) {
              onCreated(data);
            } else {
              notify("Error: Specialty creation response is invalid", {
                type: "error",
              });
            }
          },
          onError: (error: any) => {
            console.error("Create error:", error);
            notify("Error creating specialty", { type: "error" });
          },
        }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2, p: 2, border: "1px solid #eee", borderRadius: 2 }}>
      <TextField
        label="Code"
        value={formData.code}
        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Name"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Comment"
        value={formData.comment}
        onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
        fullWidth
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          Create
        </Button>
      </Box>
    </Box>
  );
};

const FilePreview = ({
  file,
  onRemove,
}: {
  file: File;
  onRemove: () => void;
}) => {
  const [pageCount, setPageCount] = useState<number | null>(null);
  const notify = useNotify();

  useEffect(() => {
    if (file && file.type === "application/pdf") {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const typedarray = new Uint8Array(e.target?.result as ArrayBuffer);
          const pdf = await pdfjsLib.getDocument({ data: typedarray }).promise;
          setPageCount(pdf.numPages);
        } catch (err) {
          notify("Could not read PDF page count", { type: "warning" });
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }, [file, notify]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
      <span>{file?.name || ""}</span>
      <span>{file ? `${(file.size / 1024).toFixed(1)} KB` : ""}</span>
      <span>{pageCount !== null ? `${pageCount} pages` : ""}</span>
      <Button color="error" size="small" onClick={onRemove}>
        Remove
      </Button>
    </Box>
  );
};

// Minimal working file upload field
const MinimalFileUploadField = ({
  onFileChange,
}: {
  onFileChange: (file: File | null) => void;
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  return (
    <div style={{ margin: "16px 0" }}>
      <label>PDF file *</label>
      <input
        type="file"
        accept="application/pdf"
        onChange={(e) => {
          const file =
            e.target.files && e.target.files[0] ? e.target.files[0] : null;
          setSelectedFile(file);
          onFileChange(file);
          console.log("[MinimalFileUploadField] selected file:", file);
        }}
      />
      {selectedFile && (
        <div>
          Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)}{" "}
          KB)
        </div>
      )}
    </div>
  );
};

export const WorkCreate = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [file, setFile] = useState<File | null>(null);
  const [author, setAuthor] = useState<any>(null);
  const [supervisor, setSupervisor] = useState<any>(null);
  const [specialty, setSpecialty] = useState<any>(null);

  const handleSubmit = async (values: any) => {
    if (!file) {
      notify("File is required", { type: "error" });
      return;
    }
    if (!values.topic || values.topic.trim().length === 0) {
      notify("Topic is required", { type: "error" });
      return;
    }
    const formData = new FormData();
    formData.append("topic", values.topic.trim());
    formData.append("authorId", author?.id || "");
    formData.append("supervisorId", supervisor?.id || "");
    formData.append("year", values.year);
    formData.append("degree", values.degree);
    formData.append("specialtyId", specialty?.id || "");
    formData.append("keywords", values.keywords);
    formData.append(
      "bibliographicDescription",
      values.bibliographicDescription
    );
    formData.append("file", file);
    try {
      await dataProvider.create("works", {
        data: formData,
      });
      notify("Work created successfully");
      redirect("list", "works");
    } catch (error) {
      console.error("Create error:", error);
      notify("Error creating work", { type: "error" });
    }
  };

  return (
    <Create>
      <SimpleForm onSubmit={handleSubmit}>
        <MinimalFileUploadField onFileChange={setFile} />
        <TextInput
          source="topic"
          label="Topic"
          fullWidth
          validate={[
            (value: string) =>
              !value || value.trim().length === 0
                ? "Topic is required"
                : undefined,
          ]}
        />
        <AuthorField author={author} setAuthor={setAuthor} />
        <SupervisorField
          supervisor={supervisor}
          setSupervisor={setSupervisor}
        />
        <NumberInput source="year" label="Year" fullWidth />
        <SelectInput
          source="degree"
          label="Degree"
          choices={degreeChoices}
          fullWidth
        />
        <SpecialtyField specialty={specialty} setSpecialty={setSpecialty} />
        <TextInput source="keywords" label="Keywords" fullWidth />
        <TextInput
          source="bibliographicDescription"
          label="Bibliographic Description"
          fullWidth
        />
      </SimpleForm>
    </Create>
  );
};
